import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, defer, tap, throwError } from 'rxjs';

import { environment } from '@env/environment';
import { AppTelemetryService, TelemetryAttributes } from '@core/observability/app-telemetry.service';

const guidSegmentPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const httpRequestTelemetryInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (!request.url.startsWith(environment.apiUrl)) {
    return next(request);
  }

  const telemetry = inject(AppTelemetryService);

  return defer(() => {
    const startedAt = performance.now();
    const requestAttributes = buildRequestAttributes(request, telemetry.getCurrentViewAttributes());

    return next(request).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          telemetry.trackEvent(
            'app.http_request.completed',
            {
              ...requestAttributes,
              statusCode: event.status,
            },
            {
              durationMs: Math.round(performance.now() - startedAt),
            },
          );
        }
      }),
      catchError((error: unknown) => {
        telemetry.trackEvent(
          'app.http_request.failed',
          {
            ...requestAttributes,
            statusCode: resolveStatusCode(error),
          },
          {
            durationMs: Math.round(performance.now() - startedAt),
          },
        );

        return throwError(() => error);
      }),
    );
  });
};

function buildRequestAttributes(
  request: HttpRequest<unknown>,
  viewAttributes: TelemetryAttributes,
): TelemetryAttributes {
  const url = new URL(request.urlWithParams, window.location.origin);

  return {
    ...viewAttributes,
    method: request.method,
    apiPath: url.pathname,
    apiPathTemplate: templatePath(url.pathname),
    queryKeys: Array.from(url.searchParams.keys()).sort().join(','),
  };
}

function templatePath(pathname: string): string {
  return pathname
    .split('/')
    .map(segment => guidSegmentPattern.test(segment) ? ':id' : segment)
    .join('/');
}

function resolveStatusCode(error: unknown): number | string {
  if (error instanceof HttpErrorResponse) {
    return error.status || 'network_error';
  }

  return 'client_error';
}
